import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import Head from 'next/head';

import ABeagle from '../components/ABeagle';
import Ads from '../components/Ads/Ads';
import Banner from '../components/banner';
import ConsentManagement from '../components/ConsentManagement';
import Footer from '../components/footer';
import Header from '../components/header';
import LinksRow from '../components/links';
import Main from '../components/main';
import PostPackage from '../components/post_package';
import TimeSpentTracking from '../components/TimeSpentTracking';
import { getBrandProperties } from '../utils/brand-properties';

import styles from './page.module.scss';
import { useAddressabilityTracking } from '../hooks/analytics/useAddressabilityTracking';
import { usePageviewTracking } from '../hooks/analytics/usePageviewTracking';
import { AccountContext } from '../contexts';

const PLATFORM_MAP = {
    'instagram': 'ig',
    'tiktok': 'tt',
    'ig': 'instagram',
    'tt': 'tiktok'
};

export function Page({ account = {}, posts, postPackage = {}, platform}) {
    const router = useRouter();

    const origin =
        typeof window !== 'undefined' && window.location.origin
            ? window.location.origin
            : '';

    useAddressabilityTracking(account);
    usePageviewTracking(account);
    const { brand } = getBrandProperties(origin);

    const [modalImage, setModalImage] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const setModalContents = useCallback((post, image) => {
        setSelectedPost(post);
        setModalImage(image);
    }, [setModalImage, setSelectedPost]);
    const toggleModal = useCallback((post, image) => {
        setModalOpen(!modalOpen);
        setModalContents(post, image);
    }, [modalOpen, setModalOpen, setModalContents]);

    if (router.isFallback) {
        return (
            <div>
                Loading...
            </div>
        );
    }

    const { title, post_a, post_b, post_c } = postPackage;
    const livePackage = title && post_a && post_b && post_c;
    const recentPosts = (posts && posts.slice()) || [];
    const packagePosts = livePackage ? [post_a, post_b, post_c] : [];
    packagePosts.forEach(pPost => {
        let index = recentPosts.findIndex(p => p.id === pPost.id);
        recentPosts.splice(index, 1);
    });

    return (
        <AccountContext.Provider value={account}>
            <ABeagle>
                <TimeSpentTracking />
                <Head>
                    <title>{account['name']} - Link in bio</title>
                    <meta
                        name="Description"
                        content="Explore your favorite BuzzFeed posts on Instagram."
                    />
                    <script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" />
                    <link rel="preconnect" crossOrigin="true" href="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js" />
                </Head>
                <ConsentManagement />
                <div className={classNames(styles.feedPage, modalOpen && styles.modalOpen)}>
                    <Ads pageType={account['name']}>
                        <Header
                            iconUrl={account['icon_image_url']}
                            account_name={account['name']}
                        />
                        <Banner hrefUrl={account['banner_link_url']}
                            imageUrl={account['banner_image_url']}
                        />
                        { livePackage &&
                        <PostPackage
                            title={title}
                            posts={packagePosts}
                            platform={platform}
                            setModalContents={setModalContents}
                            toggleModal={toggleModal}
                        />
                        }
                        { account['promo_links'] && <LinksRow links={account['promo_links']} /> }
                        <Main
                            modalImage={modalImage}
                            modalOpen={modalOpen}
                            selectedPost={selectedPost}
                            pageType={account['name']}
                            platform={platform}
                            posts={recentPosts}
                            setModalContents={setModalContents}
                            toggleModal={toggleModal}
                        />
                        <Footer brand={brand}/>
                    </Ads>
                </div>
            </ABeagle>
        </AccountContext.Provider>
    );
}

export async function getStaticProps({ params }) {
    const {
        getAccountsWithCache,
        getAccountByNameWithCache,
        getPostsByAccountId,
        getPostPackageByAccountId,
        purgeCache,
    } = require('../api/biofeed_api');
    console.log('Attempting to generate page for: ', params);
    const paramArr = params.params;
    if (paramArr[0] === 'purge') {
        console.log('Purge bio cache');
        paramArr.shift();
        purgeCache()
    }
    if (paramArr[0] === 'bio') {
        paramArr.shift();
        console.log('Remove bio from params ', params);
    }
    // Default to instagram for top level paths
    const platform = PLATFORM_MAP[paramArr[0]] || 'instagram';
    const name = paramArr[1] || paramArr[0];
    const realAccounts = await getAccountsWithCache();
    // Filter out invalid accounts before querying api
    if (!realAccounts.find((account) => account.name === name) || paramArr.length > 2) {
        console.error(`Not found in account list: ${name}`);
        return { notFound: true };
    }
    const account = await getAccountByNameWithCache(name, platform);
    if (!account) {
        console.error(`Account data failed to load for: ${platform} ${name}`);
        return { notFound: true };
    }
    const posts = await getPostsByAccountId(account.id);
    const postPackage = await getPostPackageByAccountId(account.id);
    return {
        props: {
            account,
            platform: account.platform,
            posts,
            postPackage,
        },
        revalidate: 1,
    };
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
    let paths = [];
    // Environment variables not available during Rig build/test
    if (process.env['CLUSTER']) {
        const { getAccounts } = require('../api/biofeed_api');
        const accounts = await getAccounts();
        // Get the paths we want to pre-render based on accounts
        let igRootPaths = [];
        const strictPaths = accounts.map((account) => {
            if (account.platform === 'instagram') {
                igRootPaths.push({
                    params: { params: [account.name]}
                });
            }
            return { params: { params: [PLATFORM_MAP[account.platform], account.name]} };
        });
        paths = [...strictPaths, ...igRootPaths];
    }
    // We'll pre-render only these paths at build time.
    // { fallback: blocking } will server-render pages
    // on-demand if the path doesn't exist.
    return {
        paths,
        // fallback = 'blocking'
        // - Requests that are new or invalidated will server-render before returning
        // - paths that have not been generated at build time will not result in a 404 page
        fallback: 'blocking'
    };
}

export default Page;
